import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  collectScreenContext,
  rememberContextFocus,
  type CollectedScreenContext,
} from "./collectScreenContext";
import type { PageContext } from "@/features/ai/stores/chat";
import { useChatStore } from "@/features/ai/stores/chat";
import { boundScreenContext } from "./screenContextBudget";

export function resolveRoutePage(pathname: string): {
  page: string;
  view: string;
} {
  const path = pathname.replace(/\/+$/, "") || "/";
  const segments = path.split("/").filter(Boolean);
  const last = segments.at(-1) ?? "home";

  if (path === "/dashboard") return { page: "dashboard", view: "dashboard" };
  if (path === "/home") return { page: "home", view: "home" };
  if (path.startsWith("/log/")) {
    const views: Record<string, string> = {
      emissions: "list",
      logemissions: "create",
      bulkupload: "bulk-upload",
      auditsummary: "audit-summary",
    };
    return { page: "emissions", view: views[last] ?? last };
  }
  if (path.startsWith("/targets")) {
    return {
      page: "targets",
      view:
        last === "targets"
          ? "list"
          : last === "create"
            ? "create"
            : last === "edit"
              ? "edit"
              : "detail",
    };
  }
  if (path.startsWith("/assets")) {
    return {
      page: "assets",
      view:
        last === "assets"
          ? "list"
          : last === "create"
            ? "create"
            : last === "edit"
              ? "edit"
              : "detail",
    };
  }
  if (path.startsWith("/offsets")) {
    return {
      page: "offsets",
      view:
        last === "offsets"
          ? "list"
          : last === "create"
            ? "create"
            : last === "edit"
              ? "edit"
              : "detail",
    };
  }
  if (path.startsWith("/organisation/facilities")) {
    return {
      page: "facilities",
      view:
        last === "facilities" ? "list" : last === "create" ? "create" : "edit",
    };
  }
  if (path.startsWith("/organisation/employees")) {
    return {
      page: "employees",
      view:
        last === "employees" ? "list" : last === "create" ? "create" : "edit",
    };
  }
  if (path.startsWith("/organisation/disclosures")) {
    return { page: "disclosures", view: "list" };
  }
  if (path.startsWith("/datasource")) {
    return { page: "emission factors", view: last };
  }
  if (path.startsWith("/audits/")) {
    return { page: "audits", view: last === "new" ? "create" : "list" };
  }
  if (path === "/auditsummary") return { page: "audits", view: "summary" };
  if (path.startsWith("/partners/customers/submit-data")) {
    return { page: "customers", view: "data collection" };
  }
  if (path.startsWith("/partners/customers")) {
    return { page: "customers", view: "list" };
  }
  if (path.startsWith("/partners/suppliers")) {
    return {
      page: "suppliers",
      view: last === "suppliers" ? "dashboard" : last,
    };
  }
  if (path === "/cat-map") return { page: "category mapping", view: "mapping" };
  if (path === "/resourcecentre")
    return { page: "resource centre", view: "library" };
  if (path === "/reports") return { page: "reports", view: "create" };
  if (path === "/report-asset") return { page: "reports", view: "assets" };
  if (path === "/agents") return { page: "agents", view: "activity" };
  if (path === "/bulk-upload")
    return { page: "analytics", view: "bulk-upload" };
  if (path === "/emissions/upload-activity") {
    return { page: "emissions", view: "upload-activity" };
  }
  if (path === "/analytics/briefing") {
    return { page: "analytics", view: "briefing" };
  }
  if (path.startsWith("/settings")) {
    return {
      page: "settings",
      view: segments.slice(1).join(" ") || "organisation information",
    };
  }
  if (path === "/chat") return { page: "chat", view: "conversation" };
  return { page: "application", view: path.slice(1) || "home" };
}

export function useCurrentPageContext(
  suppliedContext: PageContext | null,
): PageContext {
  const { pathname } = useLocation();
  const selectedSection = useChatStore((s) => s.contextFormSection);
  const sectionId =
    selectedSection?.pathname === pathname ? selectedSection.id : undefined;
  const route = useMemo(() => resolveRoutePage(pathname), [pathname]);
  const [collected, setCollected] = useState<
    CollectedScreenContext & { pathname: string }
  >(() => ({ ...collectScreenContext(null, route.view), pathname }));

  useEffect(() => {
    const root = document.getElementById("main-content");
    let timer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setCollected({
          ...collectScreenContext(root, route.view, sectionId),
          pathname,
        });
      }, 80);
    };
    refresh();

    const observer = root ? new MutationObserver(refresh) : undefined;
    observer?.observe(root!, {
      attributes: true,
      attributeFilter: [
        "aria-selected",
        "aria-current",
        "aria-invalid",
        "aria-checked",
        "aria-sort",
        "data-selected",
        "data-ai-record-selected",
        "data-ai-chart-summary",
        "data-ai-context-summary",
        "data-ai-active-view",
        "data-ai-record-table",
        "data-ai-metric-label",
        "data-ai-metric-value",
        "data-ai-context-truncated",
        "data-ai-form-section-open",
        "hidden",
        "aria-hidden",
        "data-ai-context-ignore",
        "style",
        "class",
      ],
      childList: true,
      characterData: true,
      subtree: true,
    });
    root?.addEventListener("input", refresh, true);
    root?.addEventListener("change", refresh, true);
    root?.addEventListener("scroll", refresh, true);
    root?.addEventListener("click", refresh, true);
    const focus = (event: Event) => {
      if (root && event.target instanceof Element)
        rememberContextFocus(root, event.target);
      refresh();
    };
    root?.addEventListener("focusin", focus, true);

    return () => {
      if (timer) clearTimeout(timer);
      observer?.disconnect();
      root?.removeEventListener("input", refresh, true);
      root?.removeEventListener("change", refresh, true);
      root?.removeEventListener("scroll", refresh, true);
      root?.removeEventListener("click", refresh, true);
      root?.removeEventListener("focusin", focus, true);
    };
  }, [pathname, route.view, sectionId]);

  return useMemo(
    () =>
      mergePageContext(
        pathname,
        suppliedContext,
        collected.pathname === pathname
          ? collected
          : collectScreenContext(null, route.view),
      ),
    [collected, pathname, route.view, suppliedContext],
  );
}

export function capturePageContext(
  pathname: string,
  suppliedContext: PageContext | null,
): PageContext {
  const selection = useChatStore.getState().contextFormSection;
  return mergePageContext(
    pathname,
    suppliedContext,
    collectScreenContext(
      document.getElementById("main-content"),
      resolveRoutePage(pathname).view,
      selection?.pathname === pathname ? selection.id : undefined,
    ),
  );
}

function mergePageContext(
  pathname: string,
  suppliedContext: PageContext | null,
  collected: CollectedScreenContext,
): PageContext {
  const route = resolveRoutePage(pathname);

  const base =
    suppliedContext?.pathname && suppliedContext.pathname !== pathname
      ? { page: route.page }
      : (suppliedContext ?? { page: route.page });
  const existing = base.screenContext;
  const screenContext = {
    ...collected.screenContext,
    ...existing,
    keyMetrics: {
      ...existing?.keyMetrics,
      ...collected.screenContext.keyMetrics,
    },
    pageTitle: existing?.pageTitle ?? collected.screenContext.pageTitle,
    activeView: collected.hasExplicitView
      ? collected.screenContext.activeView
      : (existing?.activeView ?? collected.screenContext.activeView),
    summary:
      [existing?.summary, collected.screenContext.summary]
        .filter(Boolean)
        .join("; ") || undefined,
    visibleRecords: collected.screenContext.visibleRecords,
    chartSummaries: collected.screenContext.chartSummaries,
    formFields: collected.screenContext.formFields,
    truncated:
      existing?.truncated || collected.screenContext.truncated || undefined,
  };
  const filters = {
    ...collected.filters,
    ...(base.filters ?? {}),
  };

  return {
    ...base,
    pathname,
    page: route.page,
    ...(Object.keys(filters).length ? { filters } : {}),
    screenContext: boundScreenContext(screenContext),
  };
}
